import { z } from 'zod';
import { Id } from '../primitives.js';
import { Kopecks } from './finance.js';

/**
 * Inventory: материалы и склад (веха 3.4).
 *
 * Количество — целое число милли-единиц (`packages/core/src/quantity`), не
 * `Decimal`: тот же приём, что у денег в этом файле (копейки целым числом).
 *
 * Документация модуля: docs/03-modules/14-inventory.md
 */

export const Milliquantity = z.number().int();
export type Milliquantity = z.infer<typeof Milliquantity>;

export const MATERIAL_UNIT = ['g', 'ml', 'pcs'] as const;
export const MaterialUnit = z.enum(MATERIAL_UNIT);
export type MaterialUnit = z.infer<typeof MaterialUnit>;

export const MaterialItem = z.object({
  id: Id,
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(200),
  category: z.string().min(1).max(100),
  unit: MaterialUnit,
  minQuantity: Milliquantity.nonnegative(),
  /**
   * Содержимое одной упаковки в милли-единицах базовой единицы: 500 г — это
   * 500000. Пусто, пока материал ни разу не приходил упаковками. Значение по
   * умолчанию — ради строк кэша, записанных до появления поля.
   */
  packageSize: Milliquantity.positive().nullable().default(null),
  autoWriteOff: z.boolean(),
  lastCostKopecks: Kopecks.nullable(),
  isActive: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type MaterialItem = z.infer<typeof MaterialItem>;

export const CreateMaterialItemRequest = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(200),
  category: z.string().min(1).max(100),
  unit: MaterialUnit,
  minQuantity: Milliquantity.nonnegative().default(0),
  autoWriteOff: z.boolean().default(false),
});
export type CreateMaterialItemRequest = z.infer<typeof CreateMaterialItemRequest>;

export const UpdateMaterialItemRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  category: z.string().min(1).max(100).optional(),
  minQuantity: Milliquantity.nonnegative().optional(),
  packageSize: Milliquantity.positive().nullable().optional(),
  autoWriteOff: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateMaterialItemRequest = z.infer<typeof UpdateMaterialItemRequest>;

export const STOCK_LOCATION_KIND = ['warehouse', 'cabinet'] as const;
export const StockLocationKind = z.enum(STOCK_LOCATION_KIND);
export type StockLocationKind = z.infer<typeof StockLocationKind>;

export const StockLocation = z.object({
  id: Id,
  name: z.string().min(1).max(200),
  kind: StockLocationKind,
  chairId: Id.nullable(),
  isActive: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type StockLocation = z.infer<typeof StockLocation>;

export const CreateStockLocationRequest = z.object({
  name: z.string().min(1).max(200),
  kind: StockLocationKind,
  chairId: Id.nullable().default(null),
});
export type CreateStockLocationRequest = z.infer<typeof CreateStockLocationRequest>;

export const UpdateStockLocationRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateStockLocationRequest = z.infer<typeof UpdateStockLocationRequest>;

export const StockBatch = z.object({
  id: Id,
  materialItemId: Id,
  locationId: Id,
  lotNumber: z.string().max(100).nullable(),
  supplierName: z.string().max(200).nullable(),
  receivedQuantity: Milliquantity.positive(),
  quantity: Milliquantity.nonnegative(),
  totalCostKopecks: Kopecks,
  expiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type StockBatch = z.infer<typeof StockBatch>;

export const STOCK_MOVEMENT_KIND = ['receipt', 'writeoff', 'transfer_out', 'transfer_in', 'adjustment'] as const;
export const StockMovementKind = z.enum(STOCK_MOVEMENT_KIND);
export type StockMovementKind = z.infer<typeof StockMovementKind>;

export const StockMovement = z.object({
  id: Id,
  materialItemId: Id,
  locationId: Id,
  batchId: Id.nullable(),
  kind: StockMovementKind,
  /** Со знаком: приход/увеличение положительны, расход/уменьшение отрицательны. */
  quantity: Milliquantity,
  costKopecks: Kopecks.nullable(),
  discrepancy: z.boolean(),
  expired: z.boolean(),
  reason: z.string().max(500).nullable(),
  appointmentId: Id.nullable(),
  actorId: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type StockMovement = z.infer<typeof StockMovement>;

/** Приход — заводит партию (`receiveBatch`, единственный способ). */
export const ReceiveBatchRequest = z.object({
  materialItemId: Id,
  locationId: Id,
  /** Всегда в милли-единицах базовой единицы: пересчёт из упаковок — на устройстве. */
  quantity: Milliquantity.positive(),
  totalCostKopecks: Kopecks,
  lotNumber: z.string().max(100).nullable().default(null),
  supplierName: z.string().max(200).nullable().default(null),
  expiresAt: z.iso.datetime().nullable().default(null),
  /**
   * Размер упаковки, которым приход вводили. Запоминается у материала и
   * подставляется в следующий раз; пусто — приход вводили не упаковками.
   */
  packageSize: Milliquantity.positive().nullable().default(null),
});
export type ReceiveBatchRequest = z.infer<typeof ReceiveBatchRequest>;

/** Списание вне визита — брак, недостача, использование не по норме. */
export const WriteOffRequest = z.object({
  kind: z.literal('writeoff'),
  materialItemId: Id,
  locationId: Id,
  quantity: Milliquantity.positive(),
  reason: z.string().max(500).nullable().default(null),
  /** Заполнено только у автосписания по визиту — вручную не выбирается. */
  appointmentId: Id.nullable().default(null),
});
export type WriteOffRequest = z.infer<typeof WriteOffRequest>;

/** Перемещение между точками хранения — два движения одной операцией. */
export const TransferRequest = z.object({
  kind: z.literal('transfer'),
  materialItemId: Id,
  fromLocationId: Id,
  toLocationId: Id,
  quantity: Milliquantity.positive(),
});
export type TransferRequest = z.infer<typeof TransferRequest>;

/**
 * Тело записи `inventory.stock_movement` в очереди устройства — только
 * списание и перемещение: приход принципиально требует связи (§6 спеки) и
 * идёт отдельным прямым маршрутом (`ReceiveBatchRequest`), минуя очередь.
 */
export const CreateStockMovementRequest = z.discriminatedUnion('kind', [WriteOffRequest, TransferRequest]);
export type CreateStockMovementRequest = z.infer<typeof CreateStockMovementRequest>;

export const StockBalance = z.object({
  materialItemId: Id,
  locationId: Id,
  quantity: Milliquantity,
});
export type StockBalance = z.infer<typeof StockBalance>;

export const ServiceMaterialNorm = z.object({
  id: Id,
  serviceId: Id,
  materialItemId: Id,
  quantityPerUnit: Milliquantity.positive(),
});
export type ServiceMaterialNorm = z.infer<typeof ServiceMaterialNorm>;

export const SetServiceMaterialNormRequest = z.object({
  serviceId: Id,
  materialItemId: Id,
  quantityPerUnit: Milliquantity.positive(),
});
export type SetServiceMaterialNormRequest = z.infer<typeof SetServiceMaterialNormRequest>;

// --- Заявки на закупку -------------------------------------------------------------

export const PURCHASE_REQUEST_STATUS = ['draft', 'submitted', 'approved', 'rejected', 'cancelled'] as const;
export const PurchaseRequestStatus = z.enum(PURCHASE_REQUEST_STATUS);
export type PurchaseRequestStatus = z.infer<typeof PurchaseRequestStatus>;

export const PurchaseRequestLine = z.object({
  id: Id,
  materialItemId: Id,
  quantity: Milliquantity.positive(),
  note: z.string().max(300).nullable(),
});
export type PurchaseRequestLine = z.infer<typeof PurchaseRequestLine>;

export const PurchaseRequest = z.object({
  id: Id,
  status: PurchaseRequestStatus,
  authorId: Id,
  approvedBy: Id.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  rejectReason: z.string().max(500).nullable(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
  lines: z.array(PurchaseRequestLine),
});
export type PurchaseRequest = z.infer<typeof PurchaseRequest>;

export const CreatePurchaseRequestRequest = z.object({
  lines: z
    .array(z.object({ materialItemId: Id, quantity: Milliquantity.positive(), note: z.string().max(300).nullable().default(null) }))
    .min(1),
});
export type CreatePurchaseRequestRequest = z.infer<typeof CreatePurchaseRequestRequest>;

/** Действия с заявкой в очереди устройства — тем же приёмом, что у наряда лаборатории. */
export const PurchaseRequestChange = z.discriminatedUnion('action', [
  z.object({ action: z.literal('submit') }),
  z.object({ action: z.literal('cancel') }),
]);
export type PurchaseRequestChange = z.infer<typeof PurchaseRequestChange>;

export const ApprovePurchaseRequestRequest = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve') }),
  z.object({ decision: z.literal('reject'), reason: z.string().min(1).max(500) }),
]);
export type ApprovePurchaseRequestRequest = z.infer<typeof ApprovePurchaseRequestRequest>;

/** Настраивает только владелец — `null` снимает ограничение, администратор согласовывает без лимита. */
export const SetPurchaseApprovalLimitRequest = z.object({ maxAmountKopecks: Kopecks.nullable() });
export type SetPurchaseApprovalLimitRequest = z.infer<typeof SetPurchaseApprovalLimitRequest>;

// --- Инвентаризация ------------------------------------------------------------------

export const INVENTORY_COUNT_STATUS = ['open', 'counted', 'approved', 'cancelled'] as const;
export const InventoryCountStatus = z.enum(INVENTORY_COUNT_STATUS);
export type InventoryCountStatus = z.infer<typeof InventoryCountStatus>;

export const InventoryCountLine = z.object({
  id: Id,
  materialItemId: Id,
  expectedQuantity: Milliquantity.nonnegative(),
  actualQuantity: Milliquantity.nonnegative().nullable(),
  discrepancyComment: z.string().max(500).nullable(),
});
export type InventoryCountLine = z.infer<typeof InventoryCountLine>;

export const InventoryCount = z.object({
  id: Id,
  locationId: Id,
  status: InventoryCountStatus,
  openedBy: Id,
  approvedBy: Id.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  lines: z.array(InventoryCountLine),
});
export type InventoryCount = z.infer<typeof InventoryCount>;

export const OpenInventoryCountRequest = z.object({ locationId: Id });
export type OpenInventoryCountRequest = z.infer<typeof OpenInventoryCountRequest>;

export const SetCountLineRequest = z.object({
  actualQuantity: Milliquantity.nonnegative(),
  /** Обязателен, когда факт расходится с ожидаемым — интерфейс не даёт закрыть документ без него. */
  discrepancyComment: z.string().max(500).nullable().default(null),
});
export type SetCountLineRequest = z.infer<typeof SetCountLineRequest>;

// --- Черновик автосписания по визиту ---------------------------------------------------

export const WRITE_OFF_DRAFT_STATUS = ['pending', 'confirmed'] as const;
export const WriteOffDraftStatus = z.enum(WRITE_OFF_DRAFT_STATUS);
export type WriteOffDraftStatus = z.infer<typeof WriteOffDraftStatus>;

export const WriteOffDraftLine = z.object({
  id: Id,
  materialItemId: Id,
  normQuantity: Milliquantity.nonnegative(),
  actualQuantity: Milliquantity.nonnegative(),
});
export type WriteOffDraftLine = z.infer<typeof WriteOffDraftLine>;

export const WriteOffDraft = z.object({
  id: Id,
  appointmentId: Id,
  locationId: Id,
  status: WriteOffDraftStatus,
  lines: z.array(WriteOffDraftLine),
});
export type WriteOffDraft = z.infer<typeof WriteOffDraft>;

export const ConfirmWriteOffDraftRequest = z.object({
  lines: z.array(z.object({ lineId: Id, actualQuantity: Milliquantity.nonnegative() })),
});
export type ConfirmWriteOffDraftRequest = z.infer<typeof ConfirmWriteOffDraftRequest>;

// --- Обеспеченность и себестоимость -------------------------------------------------

export const ReadinessGap = z.object({
  appointmentId: Id,
  patientId: Id,
  materialItemId: Id,
  shortfall: Milliquantity.positive(),
});
export type ReadinessGap = z.infer<typeof ReadinessGap>;

export const ReadinessReport = z.object({ date: z.iso.date(), gaps: z.array(ReadinessGap) });
export type ReadinessReport = z.infer<typeof ReadinessReport>;

export const ServiceCostEstimate = z.object({
  serviceId: Id,
  totalKopecks: Kopecks,
  materialsWithoutCost: z.array(Id),
});
export type ServiceCostEstimate = z.infer<typeof ServiceCostEstimate>;
